import React, { useCallback, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { ContextAgentContext } from "./ContextAgentContext";
import ContextAgentPanelHost from "./ContextAgentPanelHost";
import ContextAgentSidebarTab from "./ContextAgentSidebarTab";

function ContextAgentMainColumn({ children }) {
  return (
    <Box
      component="main"
      sx={{
        position: "relative",
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        flexDirection: "row",
        overflow: "hidden",
      }}
    >
      <ContextAgentSidebarTab />
      <ContextAgentPanelHost />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export default function ContextAgentProvider({ sidebar, children }) {
  const [meta, setMeta] = useState(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const [panelGeneration, setPanelGeneration] = useState(0);
  const handlersRef = useRef({
    panel: null,
    onOpenChange: null,
    onToggleClick: null,
  });
  const metaRef = useRef(null);
  metaRef.current = meta;

  const isControlled =
    meta?.open !== undefined && typeof handlersRef.current.onOpenChange === "function";

  const open = isControlled ? meta.open : internalOpen;

  const setOpen = useCallback((next) => {
    const onOpenChange = handlersRef.current.onOpenChange;
    if (onOpenChange) {
      const current = metaRef.current?.open ?? false;
      onOpenChange(typeof next === "function" ? next(current) : next);
      return;
    }
    setInternalOpen((prev) => (typeof next === "function" ? next(prev) : next));
  }, []);

  const toggleOpen = useCallback(() => {
    const onToggleClick = handlersRef.current.onToggleClick;
    if (onToggleClick) {
      onToggleClick({ open: metaRef.current?.open ?? internalOpen, setOpen });
      return;
    }
    setOpen((prev) => !prev);
  }, [setOpen, internalOpen]);

  const setRegistration = useCallback((next) => {
    if (!next) {
      handlersRef.current = { panel: null, onOpenChange: null, onToggleClick: null };
      setMeta(null);
      setInternalOpen(false);
      return;
    }

    handlersRef.current = {
      panel: next.panel ?? null,
      onOpenChange: next.onOpenChange ?? null,
      onToggleClick: next.onToggleClick ?? null,
    };
    setMeta({
      agentName: next.agentName,
      scopeLabel: next.scopeLabel,
      scopeId: next.scopeId,
      open: next.open,
    });
  }, []);

  const clearRegistration = useCallback(() => {
    setRegistration(null);
  }, [setRegistration]);

  const syncHandlers = useCallback(({ panel, onOpenChange, onToggleClick }) => {
    if (!metaRef.current) {
      return;
    }
    const prevPanel = handlersRef.current.panel;
    handlersRef.current = {
      panel: panel ?? handlersRef.current.panel,
      onOpenChange:
        onOpenChange !== undefined ? onOpenChange : handlersRef.current.onOpenChange,
      onToggleClick:
        onToggleClick !== undefined ? onToggleClick : handlersRef.current.onToggleClick,
    };
    if (prevPanel !== panel) {
      setPanelGeneration((generation) => generation + 1);
    }
  }, []);

  const value = useMemo(
    () => ({
      registration: meta
        ? {
            ...meta,
            panel: handlersRef.current.panel,
            onOpenChange: handlersRef.current.onOpenChange,
            onToggleClick: handlersRef.current.onToggleClick,
          }
        : null,
      handlersRef,
      panelGeneration,
      open,
      setOpen,
      toggleOpen,
      setRegistration,
      clearRegistration,
      syncHandlers,
      isRegistered: Boolean(meta),
    }),
    [
      meta,
      panelGeneration,
      open,
      setOpen,
      toggleOpen,
      setRegistration,
      clearRegistration,
      syncHandlers,
    ]
  );

  if (sidebar) {
    return (
      <ContextAgentContext.Provider value={value}>
        <Box
          sx={{
            display: "flex",
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflow: "hidden",
            position: "relative",
          }}
        >
          {sidebar}
          <ContextAgentMainColumn>{children}</ContextAgentMainColumn>
        </Box>
      </ContextAgentContext.Provider>
    );
  }

  return (
    <ContextAgentContext.Provider value={value}>
      <ContextAgentMainColumn>{children}</ContextAgentMainColumn>
    </ContextAgentContext.Provider>
  );
}
